import { useEffect } from 'react';

export const parallax = { x: 0, y: 0 };

export function useParallax(): void {
  useEffect(() => {
    if (typeof matchMedia !== 'function') return;
    const coarse = matchMedia('(pointer: coarse)');
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');

    let teardown: (() => void) | null = null;

    const setup = () => {
      if (coarse.matches || reduced.matches) return;

      let tx = 0;
      let ty = 0;
      let cx = 0;
      let cy = 0;
      let raf = 0;
      let running = false;
      let last = 0;

      const tick = (ts: number) => {
        const dt = last ? ts - last : 16;
        last = ts;
        const k = Math.min(1, dt * 0.006);
        cx += (tx - cx) * k;
        cy += (ty - cy) * k;
        if (Math.abs(cx - parallax.x) > 1e-4 || Math.abs(cy - parallax.y) > 1e-4) {
          document.documentElement.style.setProperty('--par-x', cx.toFixed(4));
          document.documentElement.style.setProperty('--par-y', cy.toFixed(4));
          parallax.x = cx;
          parallax.y = cy;
        }
        if (Math.abs(tx - cx) < 1e-4 && Math.abs(ty - cy) < 1e-4) {
          running = false;
          return;
        }
        raf = requestAnimationFrame(tick);
      };

      const onPointerMove = (e: PointerEvent) => {
        tx = (e.clientX / window.innerWidth) * 2 - 1;
        ty = (e.clientY / window.innerHeight) * 2 - 1;
        if (!running) {
          running = true;
          last = 0;
          raf = requestAnimationFrame(tick);
        }
      };

      window.addEventListener('pointermove', onPointerMove, { passive: true });

      teardown = () => {
        window.removeEventListener('pointermove', onPointerMove);
        cancelAnimationFrame(raf);
        document.documentElement.style.removeProperty('--par-x');
        document.documentElement.style.removeProperty('--par-y');
        parallax.x = 0;
        parallax.y = 0;
      };
    };

    const reinit = () => {
      teardown?.();
      teardown = null;
      setup();
    };

    reinit();
    coarse.addEventListener('change', reinit);
    reduced.addEventListener('change', reinit);

    return () => {
      coarse.removeEventListener('change', reinit);
      reduced.removeEventListener('change', reinit);
      teardown?.();
      teardown = null;
    };
  }, []);
}
